import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal, ModalFooter } from '../../../checklists/components/modals/Modal';

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (productCode: string) => void;
  isPending: boolean;
  isError: boolean;
}

export function AddProductModal({
  isOpen,
  onClose,
  onSubmit,
  isPending,
  isError,
}: AddProductModalProps) {
  const { t } = useLanguage();
  const [productCode, setProductCode] = useState('');

  const handleSubmit = () => {
    const code = productCode.trim().replace(/\D/g, '');
    if (!code || code.length < 3) return;
    onSubmit(code);
  };

  const handleClose = () => {
    setProductCode('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('watchlist.addProduct', 'Add Product')}
      icon={Plus}
      iconColor="primary"
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="watchlist-product-code" className="block text-sm font-medium text-gray-700 mb-2">
            {t('watchlist.productCodeLabel', 'IKEA Product Code')}
          </label>
          <input
            id="watchlist-product-code"
            type="text"
            value={productCode}
            onChange={e => setProductCode(e.target.value)}
            placeholder={t('watchlist.productCodePlaceholder', 'e.g., 40299885')}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
          />
          <p className="mt-2 text-sm text-gray-500">
            {t('watchlist.productCodeHint', 'Enter the product code from the IKEA product page (numbers only)')}
          </p>
        </div>

        {isError && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-xl">
            {t('watchlist.addError', 'Failed to add product. Please check the code and try again.')}
          </p>
        )}

        <ModalFooter>
          <button
            onClick={handleClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !productCode.trim()}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('watchlist.addToWatchlist', 'Add to Watchlist')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
